import type { TokenDelivery } from '../types/auth';

export interface StoredTokens {
  accessToken: string;
  /** Absolute epoch milliseconds at which the access token stops being valid. */
  accessExpiresAt: number;
  /** Null when the server keeps the refresh token in an HttpOnly cookie. */
  refreshToken: string | null;
  deviceId: string;
}

/**
 * Where a client keeps its credentials.
 *
 * The web client uses the in-memory implementation below and lets the browser
 * hold the refresh token in a cookie it cannot read. A React Native client
 * implements this same interface over the platform keystore. Making it an
 * interface is what lets one API client serve both without either one
 * inheriting the other's storage assumptions.
 */
export interface TokenStore {
  read(): StoredTokens | null | Promise<StoredTokens | null>;
  write(tokens: StoredTokens): void | Promise<void>;
  clear(): void | Promise<void>;
  /** Which delivery mode this store expects the server to use. */
  readonly delivery: TokenDelivery;
}

/**
 * Keeps the access token in memory only.
 *
 * Nothing is written to `localStorage` or `sessionStorage`: a token there
 * survives the tab, is readable by any script on the page and is a standing
 * prize for any cross-site scripting bug. The cost is that a page reload has
 * to spend one refresh call to get a new access token, which is the intended
 * trade.
 */
export class MemoryTokenStore implements TokenStore {
  readonly delivery: TokenDelivery = 'cookie';
  private tokens: StoredTokens | null = null;

  constructor(delivery: TokenDelivery = 'cookie') {
    this.delivery = delivery;
  }

  read(): StoredTokens | null {
    return this.tokens;
  }

  write(tokens: StoredTokens): void {
    this.tokens = tokens;
  }

  clear(): void {
    this.tokens = null;
  }
}
